import { api } from '../api.js';
import { state, setProject } from '../state.js';
import { project as store, loadProject, refreshFiles, openSessions } from '../project-store.js';
import { navigate, route, RouterLink } from '../router.js';
import { Cards } from './Cards.js';
import { Progress } from './Progress.js';
import { humanDate, humanDuration, scoreColor, plural, Markdown } from '../ui.js';
import { ensureDictionaries } from '../bootstrap.js';

export const Project = {
  components: { RouterLink, Markdown, Cards, Progress },
  data() {
    return {
      // Здесь — только состояние экрана: формы, переключатели, «занято».
      // Всё, что видят и другие вкладки, живёт в project-store.js.
      kind: 'technical',
      duo: false,
      vacancy: '',
      showVacancy: false,
      busyVacancy: false,
      vacancyStatus: '',
      vacancyRead: '',
      starting: false,
      // Сколько уже готовимся. Сценарий формата модель пишет до первой реплики,
      // и это полторы минуты тишины: без счётчика неотличимо от «зависло».
      startedS: 0,
      startTimer: null,
      busyFiles: false,
      dragging: false,
      saved: '',
      showBriefing: false,
    };
  },
  computed: {
    // Данные проекта общие для всех его вкладок и лежат в сторе. Здесь только
    // короткие переходники, чтобы разметка осталась прежней.
    loading() { return store.loading; },
    error() { return store.error; },
    project() { return store.info; },
    // Заметку правят прямо в поле, поэтому у переходника есть и запись:
    // computed без сеттера молча теряет ввод — переключил вкладку и обратно,
    // а набранного нет.
    context: {
      get() { return store.context; },
      set(v) { store.context = v; },
    },
    files() { return store.files; },
    briefing() { return store.briefing; },
    weak() { return store.weak; },
    sessions() { return store.sessions; },
    sessionsLoaded() { return store.sessionsLoaded; },
    sessionKind() { return store.sessionKind; },
    sessionPage() { return store.sessionPage; },
    sessionPages() { return store.sessionPages; },
    sessionTotal() { return store.sessionTotal; },
    sessionShown() { return store.sessionShown; },

    /**
     * Фильтры по формату. Показываем только те, что в этом проекте есть:
     * вкладка с нулём — это не фильтр, а тупик, и место она занимает как
     * настоящая.
     */
    sessionFilters() {
      const counts = store.sessionCounts || {};
      const rows = state.kinds
        .filter((k) => counts[k.id])
        .map((k) => ({ id: k.id, name: k.name, n: counts[k.id] }));
      return [{ id: '', name: 'Все', n: store.sessionTotal }, ...rows];
    },

    /**
     * Номера страниц. Больше семи подряд не рисуем: дальше это не навигация, а
     * стена цифр — края и окно вокруг текущей.
     */
    pager() {
      const last = this.sessionPages;
      const at = this.sessionPage;
      if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
      const near = [at - 1, at, at + 1].filter((n) => n > 1 && n < last);
      const out = [1, ...near, last];
      const withGaps = [];
      for (let i = 0; i < out.length; i += 1) {
        if (i && out[i] - out[i - 1] > 1) withGaps.push('…');
        withGaps.push(out[i]);
      }
      return withGaps;
    },
    due() { return store.due; },
    tracks() { return state.dictionaries.tracks; },
    grades() { return state.dictionaries.grades; },
    agents() { return state.dictionaries.agents || []; },
    personalities() { return state.dictionaries.personalities || []; },
    // Где проверялись агенты: local — здесь же, runner — на машине человека.
    agentsAt() { return state.dictionaries.agentsAt || 'local'; },
    agentPlace() { return state.dictionaries.agentPlace || 'local'; },

    /** Какая вкладка проекта открыта: адрес у каждой свой. */
    tab() {
      const m = /\/project\/\d+\/(\w+)/.exec(route.path);
      return m ? m[1] : 'main';
    },
    agentId() { return this.project?.agent || state.dictionaries.defaults?.agent || 'codex'; },
    agentInfo() { return this.agents.find((a) => a.id === this.agentId); },
    models() { return this.agentInfo?.models || []; },
    efforts() { return this.agentInfo?.efforts || []; },
    customModel() {
      const m = this.project?.model;
      return m && !this.models.some((x) => x.id === m);
    },
    kinds() { return state.kinds; },

    /**
     * Форматы двумя группами. Собеседование и тренировка — разные намерения:
     * первое проходят, второе отрабатывают, и мешать их в одном списке значит
     * заставлять человека каждый раз перечитывать все семь.
     */
    groups() {
      const of = (g) => this.kinds.filter((k) => (k.group || 'interview') === g);
      return [
        { id: 'interview', title: 'Собеседование', note: 'как на настоящем', rows: of('interview') },
        { id: 'practice', title: 'Тренировка', note: 'отработать узкое место', rows: of('practice') },
      ].filter((g) => g.rows.length);
    },
    kindInfo() { return this.kinds.find((k) => k.id === this.kind); },
    /** Свежие восемь: остальные разворачиваются кнопкой. */
    /**
     * У форматов со сценарием старт дольше: до первой реплики модель сочиняет
     * инцидент или артефакт с закладками. Молчаливое «Запускаю…» на минуту
     * читается как зависшая кнопка.
     */
    startingLabel() {
      const what = ['incident', 'review', 'pressure'].includes(this.kind)
        ? 'Готовлю сценарий' : 'Запускаю';
      // Секунды показываем не сразу: на быстром старте мигающая цифра только
      // дёргает глаз.
      return this.startedS > 5 ? `${what}… ${this.startedS}с` : `${what}…`;
    },
    canDrill() { return this.weak.length > 0; },
    vacancyHasLink() { return /https?:\/\//.test(this.vacancy); },
    /** Сколько уже вставлено: по этому видно, дошло ли содержимое вообще. */
    vacancyChars() {
      const n = this.vacancy.trim().length;
      if (!n) return '';
      return n < 400 ? `${n} знаков` : `${Math.round(n / 100) / 10} тыс. знаков`;
    },
    trackName() { return this.tracks.find((t) => t.id === this.project?.track)?.name || ''; },
    gradeName() { return this.grades.find((g) => g.id === this.project?.grade)?.name || ''; },
  },
  // Перечитывать проект на смену адреса не надо: вкладки — это тоже смена
  // адреса, а страница у них одна. Другой проект — другая страница, её создаёт
  // заново сам роутер.
  async created() { await ensureDictionaries(); await this.load(); },
  methods: {
    humanDate, humanDuration, scoreColor, plural,

    kindLabel(id) { return this.kinds.find((k) => k.id === id)?.name || id; },

    /** Переход на вкладку проекта из текста: ссылка внутри плитки выбора. */
    go(tab) { navigate(`/project/${this.project.id}/${tab}`); },

    /** Другой формат в списке: страница всегда сбрасывается на первую. */
    pickKind(kind) { return openSessions({ kind, page: 1 }); },

    toPage(n) { return openSessions({ page: n }); },

    // Незакрытый собес ведёт обратно в разговор: на странице разбора у него
    // ещё нечего показывать.
    linkFor(s) {
      return s.status === 'active' ? `/interview/${s.id}` : `/session/${s.id}`;
    },

    // «другая…» только раскрывает поле ввода: сохранять пустую строку как
    // модель нельзя, иначе сбросится выбор.
    onModel(value) {
      if (value === '__custom') { this.project.model = ' '; return; }
      this.patch({ model: value });
    },

    // Смена агента подставляет его первую модель: пустая модель означала бы
    // «сервер разберётся», а в интерфейсе всегда должно быть видно, чем именно
    // проводится собес.
    onAgent(id) {
      // Модель сбрасываем: у другого агента она другая, а какая именно —
      // расскажет раннер. Пустая означает «по умолчанию агента».
      const first = this.agents.find((a) => a.id === id)?.models?.[0]?.id || '';
      this.patch({ agent: id, model: first });
    },

    async load() {
      const id = Number(route.params.id);
      if (!id) return;
      setProject(id);
      await loadProject(id);
    },

    async patch(fields) {
      await api.patch(`/api/projects/${this.project.id}`, fields);
      Object.assign(store.info, fields);
      const inList = state.projects.find((p) => p.id === this.project.id);
      if (inList) Object.assign(inList, fields);
      this.flash('сохранено');
    },

    flash(msg) {
      this.saved = msg;
      setTimeout(() => { this.saved = ''; }, 2000);
    },

    async saveContext() {
      await api.put(`/api/projects/${this.project.id}/context`, { text: this.context });
      this.flash('контекст сохранён — не забудь пересобрать материалы');
    },

    /** Перетащенные файлы идут тем же путём, что и выбранные в диалоге. */
    onDrop(e) {
      this.dragging = false;
      const files = [...(e.dataTransfer?.files || [])];
      if (files.length) this.upload({ target: { files, value: '' } });
    },

    async upload(event) {
      const list = [...event.target.files];
      if ('value' in event.target) event.target.value = '';
      if (!list.length) return;
      this.busyFiles = true;
      store.error = '';
      try {
        for (const file of list) {
          try {
            await api.raw(`/api/projects/${this.project.id}/files`, await file.arrayBuffer(),
              { 'x-filename': encodeURIComponent(file.name) });
          } catch (e) {
            store.error = `${file.name}: ${e.message}`;
          }
        }
        await this.rebuild();
      } finally {
        this.busyFiles = false;
      }
    },

    async removeFile(file) {
      await api.del(`/api/projects/${this.project.id}/files/${file.id}`);
      store.files = store.files.filter((f) => f.id !== file.id);
    },

    async rebuild() {
      this.busyFiles = true;
      try {
        const { briefing } = await api.post(`/api/projects/${this.project.id}/briefing`);
        store.briefing = briefing ? { body: briefing, built_at: new Date().toISOString() } : null;
        this.flash('материалы разобраны');
      } catch (e) {
        store.error = 'выжимка не собралась: ' + e.message;
      } finally {
        // Список обновляем в любом случае. Выжимку собирает агент, и она может
        // не собраться — например, раннер не запущен. Но файл-то уже загружен,
        // и если список не обновить, человек решит, что загрузка не удалась,
        // и будет грузить снова.
        await refreshFiles();
        this.busyFiles = false;
      }
    },

    /**
     * Файл разбирает агент: формат заранее неизвестен, и угадывать его на
     * нашей стороне бессмысленно. В материалы проекта он не идёт — вакансия
     * относится к одному собесу, а материалы к кандидату.
     */
    async vacancyFile(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.busyVacancy = true;
      this.vacancyStatus = `разбираю ${file.name}, это займёт до минуты…`;
      try {
        const res = await api.raw(`/api/projects/${this.project.id}/vacancy/file`,
          await file.arrayBuffer(), {
            'x-filename': encodeURIComponent(file.name),
            'x-note': encodeURIComponent(this.vacancy.trim()),
          });
        this.vacancy = res.vacancy;
        this.vacancyRead = res.vacancy;
        this.vacancyStatus = 'разобрано, вакансия на месте';
      } catch (e) {
        this.vacancyStatus = 'не вышло: ' + e.message;
      } finally {
        this.busyVacancy = false;
      }
    },

    /**
     * Ссылку читаем сами, как только человек ушёл из поля.
     *
     * Кнопка «Прочитать» осталась, но нажимать её было не за чем: без чтения
     * интервьюер видит голый адрес и додумывает вакансию, а понять это можно
     * только по итогу собеса. Читаем один раз на ссылку: повторное касание
     * поля не должно снова дёргать агента.
     */
    async autoRead() {
      if (!this.vacancyHasLink || this.busyVacancy) return;
      if (this.vacancyRead === this.vacancy) return;
      this.vacancyRead = this.vacancy;
      await this.readVacancy();
    },

    async readVacancy() {
      this.busyVacancy = true;
      this.vacancyStatus = 'открываю ссылку, это займёт до минуты…';
      try {
        const { vacancy } = await api.post(`/api/projects/${this.project.id}/vacancy`,
          { text: this.vacancy });
        this.vacancy = vacancy;
        this.vacancyRead = vacancy;
        this.vacancyStatus = 'прочитано, вакансия на месте';
      } catch (e) {
        this.vacancyStatus = 'не вышло: ' + e.message;
      } finally {
        this.busyVacancy = false;
      }
    },

    async start() {
      this.starting = true;
      store.error = '';
      this.startedS = 0;
      clearInterval(this.startTimer);
      this.startTimer = setInterval(() => { this.startedS += 1; }, 1000);
      try {
        const { session } = await api.post('/api/interview/start', {
          project: this.project.id,
          kind: this.kind,
          duo: this.duo,
          voice: state.voice || state.tts.voice,
          vacancy: this.vacancy.trim(),
        });
        state.activeSession = session;
        navigate(`/interview/${session.id}`);
      } catch (e) {
        store.error = e.message;
        this.starting = false;
      } finally {
        clearInterval(this.startTimer);
        this.startTimer = null;
      }
    },
  },
  unmounted() { clearInterval(this.startTimer); },
  template: `
    <div class="page" v-if="project">
      <p class="crumbs"><router-link to="/projects">Проекты</router-link></p>
      <div class="row session-head">
        <div class="who">
          <h1>{{ project.name }}</h1>
          <p class="sub">
            {{ trackName }} · {{ gradeName }}<template v-if="sessionsLoaded">
            · {{ sessionShown }} {{ plural(sessionShown, 'собес', 'собеса', 'собесов') }}</template>
          </p>
        </div>
      </div>

      <!-- Повторы, прогресс и материалы — это про этот проект, а не про всё
           приложение: соседний проект о них не знает. Поэтому они вкладки
           здесь, а не пункты в общей шапке, и страница перестала быть
           вертикальной свалкой из восьми блоков подряд. -->
      <nav class="tabs">
        <router-link :to="'/project/' + project.id">
          <button :class="{ on: tab === 'main' }">Собеседования</button>
        </router-link>
        <router-link :to="'/project/' + project.id + '/cards'">
          <button :class="{ on: tab === 'cards' }">
            Повторы<b class="count" v-if="due"> {{ due }}</b>
          </button>
        </router-link>
        <router-link :to="'/project/' + project.id + '/progress'">
          <button :class="{ on: tab === 'progress' }">Прогресс</button>
        </router-link>
        <router-link :to="'/project/' + project.id + '/materials'">
          <button :class="{ on: tab === 'materials' }">Материалы</button>
        </router-link>
        <router-link :to="'/project/' + project.id + '/setup'">
          <button :class="{ on: tab === 'setup' }">Настройки</button>
        </router-link>
      </nav>

      <p class="err" v-if="error">{{ error }}</p>

      <!-- Вкладки не пересоздаются при переключении: они грузят своё один раз
           при открытии проекта и дальше просто показываются. Иначе каждый
           переход — новый запрос, мигание и потеря того, что было набрано. -->
      <div class="tab-body tab-cards" v-show="tab === 'cards'"><Cards></Cards></div>
      <div class="tab-body tab-progress" v-show="tab === 'progress'"><Progress></Progress></div>

      <div class="tab-body tab-main" v-show="tab === 'main'">

      <div class="card">
        <p class="cover-sub" style="margin-top:0">Формат</p>

        <!-- Семь одинаковых плиток в случайном порядке — это не выбор, а
             витрина: человек не понимает, чем они отличаются и что брать.
             Поэтому список: две группы с понятными названиями, строки вместо
             плиток, и в каждой строке — когда этот формат берут. -->
        <div class="formats" v-for="g in groups" :key="g.id">
          <div class="fgroup">{{ g.title }} <i>{{ g.note }}</i></div>
          <button v-for="k in g.rows" :key="k.id" class="format" :data-kind="k.id"
                  :class="{ on: kind === k.id }"
                  :disabled="k.id === 'drill' && !canDrill"
                  @click="kind = k.id">
            <span class="mark"></span>
            <span class="body">
              <span class="top">
                <b>{{ k.name }}</b>
                <i>{{ k.minutes }} мин</i>
              </span>
              <!-- Счёт слабых тем идёт в описании, а не отдельным значком в
                   заголовке: значок переносил «15 мин» на вторую строку, и
                   единственная карточка в ряду выглядела выше остальных. Сами
                   темы — в подсказке: списком они растили карточку на три
                   строки, а нужны раз в жизни. -->
              <span class="what">
                {{ k.hint }}
                <b class="count" v-if="k.id === 'drill' && canDrill"
                   :title="weak.map(w => w.topic).join(', ')">Сейчас их {{ weak.length }}.</b>
              </span>
              <!-- Перечень слабых тем стоял прямо здесь и растил карточку на
                   три строки: соседние в ряду становились разной высоты, а
                   экран — длиннее на пустом месте. Держим короткую цифру, а
                   сами темы человек видит во вкладке «Прогресс», где им и
                   место. -->
              <span class="why" v-if="k.id === 'drill' && !canDrill">
                нужен хотя бы один разобранный собес — оценок пока нет
              </span>
            </span>
          </button>
        </div>

        <label class="check">
          <input id="duo" type="checkbox" v-model="duo">
          <span>
            Двое интервьюеров
            <i class="faint">Ведущий и второй, с другим голосом и характером.
            Держать разговор с двумя тяжелее: они перебивают и уводят в разные
            стороны, как на настоящем собесе.</i>
          </span>
        </label>

        <!-- Раньше здесь была серая ссылка «+ Под конкретную вакансию» под
             кнопкой старта. Её не видели, а кто видел — не понимал, чем она
             отличается от материалов проекта: там ведь тоже сказано «описание
             вакансии». Теперь это явный выбор из двух, и разница названа
             прямо: материалы про тебя и живут всегда, вакансия про место и
             живёт один разговор. -->
        <p class="cover-sub">Под кого спрашивать</p>
        <div class="formats aims">
          <button class="format" :class="{ on: !showVacancy }" @click="showVacancy = false">
            <span class="mark"></span>
            <span class="body">
              <span class="top">
                <b>По твоему профилю</b>
                <i v-if="files.length">{{ files.length }} {{ plural(files.length, 'файл', 'файла', 'файлов') }}</i>
                <i v-else>материалов нет</i>
              </span>
              <span class="what">
                Спрашивают по направлению и уровню проекта, опираясь на то, что
                лежит во вкладке
                <!-- Ссылка, а не название: «материалы» без неё звучит как
                     что-то абстрактное, и человек не понимает, что именно он
                     выбирает и где на это посмотреть. -->
                <a :href="'/project/' + project.id + '/materials'"
                   @click.stop.prevent="go('materials')">Материалы</a> —
                резюме, сетка грейдов, код. Это про тебя и остаётся на все собесы.
              </span>
            </span>
          </button>
          <button class="format" :class="{ on: showVacancy }" @click="showVacancy = true">
            <span class="mark"></span>
            <span class="body">
              <span class="top">
                <b>Под конкретную вакансию</b>
                <i v-if="vacancyChars">{{ vacancyChars }}</i>
              </span>
              <span class="what">
                Спрашивают по её требованиям и с той глубиной, которую они
                предполагают. Это про место, куда идёшь, и только на этот
                разговор: во вкладку «Материалы» вакансия не попадает.
              </span>
            </span>
          </button>
        </div>

        <div class="field vac" v-if="showVacancy">
          <textarea v-model="vacancy" rows="5" @blur="autoRead"
                    placeholder="вставь текст вакансии или ссылку на неё — ссылку прочитаю сам"></textarea>

          <div class="row">
            <input type="file" ref="vacFile" style="display:none" @change="vacancyFile">
            <button class="sm" @click="$refs.vacFile.click()" :disabled="busyVacancy">
              Файл: pdf, docx, скриншот
            </button>
            <button class="sm ghost" @click="readVacancy"
                    :disabled="busyVacancy || !vacancyHasLink">
              Прочитать ссылку
            </button>
            <button class="sm ghost" @click="vacancy = ''; vacancyStatus = ''"
                    v-if="vacancy.trim()" :disabled="busyVacancy">
              Очистить
            </button>
            <span class="spacer"></span>
            <span class="faint" :class="{ busy: busyVacancy }">{{ vacancyStatus }}</span>
          </div>

          <p class="faint" v-if="vacancyHasLink && !busyVacancy">
            Ссылку надо открыть до начала разговора: на собесе у интервьюера
            инструментов нет, и голый адрес он только додумает. Уйдёшь из поля —
            прочитаю сам.
          </p>
        </div>

        <div class="row" style="margin-top:18px">
          <button class="primary" @click="start" :disabled="starting">
            {{ starting ? startingLabel : 'Начать собеседование' }}
          </button>
          <span class="faint" v-if="showVacancy && !vacancy.trim()">
            вакансия пустая — собес пойдёт по профилю
          </span>
        </div>
      </div>

      <h2>Прошлые собесы</h2>

      <div class="card" v-if="sessionsLoaded && !sessions.length">
        <p class="muted" style="margin:0">
          Пока ни одного. После первого появятся оценки по темам, запись разговора и тренажёр.
        </p>
      </div>

      <template v-else-if="sessionTotal">
        <!-- Фильтр по формату: у активного пользователя в списке вперемешку
             техничка, тренажёр и инциденты, а ищут обычно одно из них. -->
        <div class="chips" v-if="sessionFilters.length > 2">
          <button v-for="f in sessionFilters" :key="f.id || 'all'"
                  class="chip" :class="{ on: sessionKind === f.id }"
                  @click="pickKind(f.id)">
            {{ f.name }}<i>{{ f.n }}</i>
          </button>
        </div>

      <div class="sessions">
        <router-link v-for="s in sessions" :key="s.id" :to="linkFor(s)" class="session-row">
          <span class="seq">#{{ s.seq || s.id }}</span>
          <span class="main">
            <b>{{ kindLabel(s.kind) }}</b>
            <span class="faint">
              {{ humanDate(s.started_at) }}<template v-if="s.duration_s"> · {{ humanDuration(s.duration_s) }}</template> · {{ s.grade }}
            </span>
          </span>
          <span class="marks">
            <!-- Пока собирается разбор, собес формально ещё активен, но
                 возвращаться в него незачем: разговор уже кончился. -->
            <span class="tag" v-if="s.wrap">собираю разбор…</span>
            <span class="tag warn" v-else-if="s.status === 'active'">идёт · продолжить</span>
            <span class="tag" v-else-if="s.status === 'canceled'">отменён</span>
            <span class="tag ok" v-if="Number(s.scores)">
              {{ s.scores }} {{ plural(Number(s.scores), 'оценка', 'оценки', 'оценок') }}
            </span>
            <span class="tag" v-if="s.has_audio"><span class="ico" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M4 6h2l3-2.5v9L6 10H4z"/><path d="M11 5.5a3.5 3.5 0 0 1 0 5"/></svg></span> запись</span>
          </span>
        </router-link>
      </div>

        <!-- Страницы, а не «показать все»: список растёт неограниченно, и
             грузить сотню строк ради последних пяти незачем. -->
        <div class="pager" v-if="sessionPages > 1">
          <button class="sm ghost" :disabled="sessionPage === 1"
                  @click="toPage(sessionPage - 1)">←</button>
          <template v-for="(n, i) in pager" :key="i">
            <span class="gap" v-if="n === '…'">…</span>
            <button v-else class="sm" :class="{ on: n === sessionPage }"
                    @click="toPage(n)">{{ n }}</button>
          </template>
          <button class="sm ghost" :disabled="sessionPage === sessionPages"
                  @click="toPage(sessionPage + 1)">→</button>
          <span class="faint">{{ sessionShown }} {{ plural(sessionShown, 'собес', 'собеса', 'собесов') }}</span>
        </div>
      </template>

      </div>

      <div class="tab-body tab-materials" v-show="tab === 'materials'">
      <div class="card">
        <p class="note" style="margin-top:0">
          Резюме, сетка грейдов, описание вакансии. Без них интервьюер не знает
          о тебе ничего и выясняет всё вопросами.
        </p>

        <!-- Файлы кладут перетаскиванием чаще, чем через диалог: раньше сюда
             можно было попасть только кнопкой, и перетащенный файл браузер
             просто открывал вместо страницы. -->
        <div class="drop" :class="{ over: dragging, busy: busyFiles }"
             @dragover.prevent="dragging = true"
             @dragleave.prevent="dragging = false"
             @drop.prevent="onDrop">
          <input type="file" ref="picker" multiple style="display:none" @change="upload">
          <b v-if="busyFiles">Читаю файлы, это может занять минуту…</b>
          <template v-else>
            <b>Перетащи файлы сюда</b>
            <span>или <a href="#" @click.prevent="$refs.picker.click()">выбери на диске</a>
              · pdf, docx, xlsx, csv, md, txt</span>
          </template>
        </div>

        <div class="files" v-if="files.length">
          <div v-for="f in files" :key="f.id" class="file">
            <span class="name">{{ f.name }}</span>
            <span class="faint sm">{{ Math.round(f.chars / 1000) }}k знаков</span>
            <button class="ghost sm" @click="removeFile(f)" title="убрать">×</button>
          </div>
        </div>

        <div class="field" style="margin-top:14px">
          <label>Ссылки, доступы и заметки</label>
          <textarea v-model="context" @blur="saveContext"
                    placeholder="напр.: репозиторий https://oauth2:ТОКЕН@gitlab.example.ru/me/infra.git — посмотри код&#10;вакансия https://hh.ru/vacancy/123&#10;слабо в мониторинге"></textarea>
        </div>

        <div class="row" style="margin-top:14px;gap:10px" v-if="files.length || briefing">
          <span class="faint" v-if="briefing">
            Выжимка готова, {{ briefing.body.length }} знаков — интервьюер её видит.
            <a href="#" @click.prevent="showBriefing = !showBriefing">{{ showBriefing ? 'скрыть' : 'посмотреть' }}</a>
          </span>
          <span class="faint" v-else>Выжимки нет — собери её из приложенных файлов.</span>
          <span class="spacer"></span>
          <button class="ghost sm" @click="rebuild" :disabled="busyFiles">Пересобрать выжимку</button>
        </div>
        <p class="faint" v-else-if="!busyFiles" style="margin-top:14px">
          Файлов нет: интервьюер о тебе ничего не знает.
        </p>
        <div class="card" v-if="showBriefing && briefing" style="margin-top:10px">
          <Markdown :text="briefing.body" />
        </div>
      </div>

      </div>

      <div class="tab-body tab-setup" v-show="tab === 'setup'">
      <div class="card">
        <div class="row" style="gap:12px">
          <div style="flex:1">
            <label>Направление</label>
            <select :value="project.track" @change="patch({ track: $event.target.value })">
              <option v-for="t in tracks" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div style="flex:1">
            <label>Грейд</label>
            <select :value="project.grade" @change="patch({ grade: $event.target.value })">
              <option v-for="g in grades" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
        </div>
        <p class="faint" style="margin-top:10px">
          Направление — область вопросов, грейд — строгость и зона ответственности.
        </p>

        <div class="field" style="margin-top:12px">
          <label>Характер интервьюера</label>
          <select :value="project.personality" @change="patch({ personality: $event.target.value })">
            <option v-for="p in personalities" :key="p.id" :value="p.id">{{ p.name }} — {{ p.hint }}</option>
          </select>
          <p class="faint">Меняет манеру, а не строгость: вопросы одинаково глубокие.</p>
        </div>

        <label class="check" style="margin-top:14px">
          <input type="checkbox" :checked="project.coding === true"
                 @change="patch({ coding: $event.target.checked })">
          <span>Давать задачу на код</span>
        </label>
        <p class="faint">
          Просят в середине разговора; пропустить можно прямо на собесе.
          В скрининге и тренажёре кода не бывает.
        </p>
      </div>

      <div class="card">
        <h3 style="margin-top:0">Кто проводит собеседование</h3>
        <p class="note" style="margin-top:0">
          Собес ведёт агент под твоей подпиской<template v-if="agentPlace === 'server'">
          — сейчас на твоём сервере</template><template v-else> — на твоей машине</template>.
          Сервер его не запускает: подключать нечего, нужен только вход в него.
          Где он сейчас и жив ли — <router-link to="/settings/agent">на странице «Агент»</router-link>.
        </p>

        <div class="row wrap" style="gap:12px;margin-top:12px">
          <div style="flex:1;min-width:200px">
            <label>Агент</label>
            <select :value="agentId" @change="onAgent($event.target.value)">
              <option v-for="a in agents" :key="a.id" :value="a.id"
                      :disabled="a.known && !a.version">
                {{ a.name }}{{ a.known && !a.version ? ' — не установлен' : '' }}
              </option>
            </select>
          </div>
          <div style="flex:1;min-width:200px">
            <label>Модель</label>
            <!-- Одно поле, а не два. Раньше выбор «другая…» открывал ещё одну
                 строку «Идентификатор модели» ниже по странице: на экране
                 оказывались два поля под одно значение, и какое из них главное
                 — непонятно. Теперь ввод встаёт на место списка. -->
            <select v-if="models.length && !customModel" :value="project.model"
                    @change="onModel($event.target.value)">
              <option v-for="m in models" :key="m.id" :value="m.id">{{ m.name }}</option>
              <option value="__custom">другая…</option>
            </select>
            <!-- Список моделей приходит от раннера: они зависят от того, что
                 установлено на машине человека. Пока он не приходил, выдумывать
                 список нельзя — можно только вписать модель руками. -->
            <input v-else :value="project.model.trim()"
                   @blur="patch({ model: $event.target.value.trim() })"
                   placeholder="напр. gpt-5.6-terra">
            <a href="#" class="faint sm" v-if="customModel && models.length"
               @click.prevent="onModel(models[0].id)">← выбрать из списка</a>
          </div>
          <div style="flex:1;min-width:160px" v-if="efforts.length">
            <label>Глубина рассуждения</label>
            <select :value="project.effort" @change="patch({ effort: $event.target.value })">
              <option v-for="e in efforts" :key="e" :value="e">{{ e }}</option>
            </select>
          </div>
        </div>

        <!-- Пока раннер не приходил, про каждого агента писать «ещё не
             проверялся» — это две строки ни о чём. Одной хватает. -->
        <p class="faint sm" style="margin-top:10px" v-if="!agents.some(a => a.known)">
          Что установлено, расскажет раннер, когда подключится.
        </p>
        <div v-for="a in agents" :key="'st' + a.id" v-else
             class="row" style="margin-top:8px;font-size:13px">
          <span class="tag" :class="!a.known ? '' : (a.version ? 'ok' : 'bad')">{{ a.name }}</span>
          <span class="faint" v-if="a.version">{{ a.version }}</span>
          <span class="faint" v-else-if="!a.known">не проверялся</span>
          <span class="faint" v-else>не найден в PATH · <a :href="a.install" target="_blank">как поставить</a></span>
        </div>

      </div>
      </div>
    </div>`,
};
